import type { BlockBase, BlockDefinition, BlockSerialized } from '#layers/block-engine/types/block';
import { createSharedComposable, useMemoize } from '@vueuse/core';
import { computed, reactive, readonly, ref } from 'vue';
import { useBlockAdapter } from '#layers/block-engine/composables/use-block-adapter';
import { getBlockConfigFromComponent } from '#layers/block-engine/utils/block/api/config';
import { getBuiltinBlocks } from '#layers/block-engine/utils/block/builtin-registry';
import { slotOutletBlockId } from '#layers/block-engine/utils/block/const';
import { useTranslationUtils } from '#layers/i18n/public';

interface UseBlockEditorLibraryUseFilteredBlocksOptions {
	filter?: (block: BlockSerialized | BlockDefinition | BlockBase) => boolean;
}

const unspecifiedCategory = 'uncategorized';
const excludedCategories = new Set([
	'space',
	'page',
]);

export const blockDefinitionTranslatableKeys = ['name', 'description'] as const;

export const useBlockEditorLibrary = createSharedComposable(_useBlockEditorLibrary);

function _useBlockEditorLibrary() {
	const { loadBlocksSerialized } = useBlockAdapter();
	const { translateObject } = useTranslationUtils();

	const workspaceBlocks = ref<BlockSerialized[]>([]);
	const builtinBlocks = ref<BlockDefinition[]>([]);
	// Tracks whether the first fetchBlocks() call has settled. Consumers that
	// need to distinguish "block not found" from "still loading" read this.
	const isReady = ref(false);

	const specialBlocks: Array<BlockBase> = [
		{
			id: slotOutletBlockId,
			name: 'Slot Outlet',
			description: 'Render content from outside the block with slots',
			category: 'special',
			icon: 'lucide:scan-text',
			hidden: true,
		},
	];

	const fetchBlocks = useMemoize(async () => {
		// Load builtin blocks
		if (builtinBlocks.value.length === 0) {
			builtinBlocks.value = Object.values(getBuiltinBlocks()).map((info) => {
				const config = getBlockConfigFromComponent(info.comp);
				return translateObject({
					...info,
					name: config.name ?? info.name,
					description: config.description,
					icon: config.icon,
					category: config.category,
					tags: config.tags,
					type: config.type,
					hidden: config.hidden,
				} satisfies BlockDefinition, { keys: blockDefinitionTranslatableKeys });
			});
		}

		try {
			// Load workspace blocks
			workspaceBlocks.value = await loadBlocksSerialized();
		}
		catch (error) {
			console.error('Failed to load workspace blocks:', error);
			workspaceBlocks.value = [];
		}

		// Allow reloading the blocks
		fetchBlocks.clear();
	});

	// useMemoize will allow reusing the same promise so we can await the loading of the blocks externally
	fetchBlocks().finally(() => {
		isReady.value = true;
	});

	function getBlock(id: string): BlockSerialized | BlockDefinition | undefined {
		return workspaceBlocks.value.find((block) => block.id === id)
			?? builtinBlocks.value.find((block) => block.id === id);
	}

	// Use a nested composable to allow customizing the filtering options in different components
	// while sharing the rest of the useBlockEditorLibrary functionality
	function useFilteredBlocks(options: UseBlockEditorLibraryUseFilteredBlocksOptions) {
		const filters = reactive(getNewFilters());

		function resetFilters() {
			Object.assign(filters, getNewFilters());
		}

		const filteredBlocks = computed(() => {
			let included: BlockBase[] = [];

			if (filters.include.builtin) {
				included.push(...specialBlocks, ...builtinBlocks.value);
			}

			if (filters.include.workspace) {
				included.push(...workspaceBlocks.value.filter((b) => !b.category || !excludedCategories.has(b.category)).toSorted((a, b) => a.name.localeCompare(b.name)));
			}

			const filter = options.filter;

			if (filter) {
				included = included.filter((block) => filter(block));
			}

			if (filters.searchText.trim()) {
				const reg = new RegExp(filters.searchText.trim(), 'i');
				included = included.filter((block) => reg.test(block.name) || reg.test(block.description ?? ''));
			}

			return included;
		});

		const blocksPerCategory = computed(() => {
			const result = new Map<string, BlockBase[]>();

			function add(block: BlockBase) {
				const category = block.category ?? unspecifiedCategory;

				if (!result.has(category)) {
					result.set(category, []);
				}

				result.get(category)!.push(block);
			}

			for (const block of filteredBlocks.value) {
				add(block);
			}

			// Sort blocks
			for (const [category, blocks] of result) {
				result.set(category, blocks.toSorted((a, b) => a.name.localeCompare(b.name)));
			}

			// Sort categories
			const sortedCategories = [...result.keys()].toSorted((a, b) => {
				if (a === unspecifiedCategory) {
					return 1;
				}

				if (b === unspecifiedCategory) {
					return -1;
				}

				return a.localeCompare(b);
			});
			const sortedResult = new Map<string, BlockBase[]>();

			for (const category of sortedCategories) {
				sortedResult.set(category, result.get(category)!);
			}

			return sortedResult;
		});

		return {
			filters,
			resetFilters,
			filteredBlocks,
			blocksPerCategory,
		};
	}

	return {
		fetchBlocks,
		getBlock,
		isReady: readonly(isReady),
		workspaceBlocks,
		builtinBlocks,
		useFilteredBlocks,
	};
}

interface Filters {
	searchText: string;
	include: {
		builtin: boolean;
		workspace: boolean;
	};
}

function getNewFilters(): Filters {
	return {
		searchText: '',
		include: {
			builtin: true,
			workspace: true,
		},
	};
}
